import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  HomeIcon, 
  ShoppingCartIcon, 
  CurrencyRupeeIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  CubeIcon,
  CogIcon,
  DocumentPlusIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Purchases', href: '/purchases', icon: ShoppingCartIcon },
  { name: 'Sales', href: '/sales', icon: CurrencyRupeeIcon },
  { name: 'Invoice Creator', href: '/invoice-creator', icon: DocumentPlusIcon },
  { name: 'Inventory', href: '/inventory', icon: CubeIcon },
  { name: 'Suppliers', href: '/suppliers', icon: BuildingOfficeIcon },
  { name: 'Reports', href: '/reports', icon: ChartBarIcon },
  { name: 'Admin Settings', href: '/admin-settings', icon: CogIcon },
  { name: 'User Management', href: '/user-management', icon: UserIcon },
  { name: 'Profile', href: '/profile', icon: UserIcon },
];

// Non-ITC Invoice Storage - Separate system
const nonITCLink = { 
  name: 'Non-ITC Invoices', 
  href: '/non-itc-invoices.html', 
  icon: DocumentTextIcon,
  external: true 
};

function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentUser, userProfile, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Failed to log out');
    }
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)}></div>
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <h1 className="text-xl font-bold text-gray-900">GST Manager</h1>
            </div>
            <nav className="mt-5 px-2 space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      isActive
                        ? 'bg-primary-100 border-primary-500 text-primary-700'
                        : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    } group flex items-center px-2 py-2 text-base font-medium border-l-4 rounded-md`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon
                      className={`${
                        isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                      } mr-4 h-6 w-6`}
                    />
                    {item.name}
                  </Link>
                );
              })}
              
              {/* Non-ITC Invoice Storage Link */}
              <div className="border-t border-gray-200 my-4"></div>
              <a
                href={nonITCLink.href}
                target="_blank"
                rel="noopener noreferrer"
                className="border-transparent text-orange-600 hover:bg-orange-50 hover:text-orange-700 group flex items-center px-2 py-2 text-base font-medium border-l-4 rounded-md"
                onClick={() => setSidebarOpen(false)}
              >
                <nonITCLink.icon className="text-orange-400 group-hover:text-orange-500 mr-4 h-6 w-6" />
                {nonITCLink.name}
              </a>
            </nav>
          </div>
          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div>
                <img
                  className="inline-block h-9 w-9 rounded-full"
                  src={currentUser?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.displayName || currentUser?.email || 'User')}&background=3b82f6&color=ffffff`}
                  alt=""
                />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                  {userProfile?.displayName || currentUser?.email}
                </p>
                <button
                  onClick={handleLogout}
                  className="text-xs font-medium text-gray-500 group-hover:text-gray-700 flex items-center"
                >
                  <ArrowRightOnRectangleIcon className="h-3 w-3 mr-1" />
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 border-r border-gray-200 bg-white">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <h1 className="text-xl font-bold text-gray-900">GST Manager</h1>
              </div>
              <nav className="mt-5 flex-1 px-2 bg-white space-y-1">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${
                        isActive
                          ? 'bg-primary-100 border-primary-500 text-primary-700'
                          : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      } group flex items-center px-2 py-2 text-sm font-medium border-l-4 rounded-md`}
                    >
                      <item.icon
                        className={`${
                          isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                        } mr-3 h-5 w-5`}
                      />
                      {item.name}
                    </Link>
                  );
                })}
                
                {/* Non-ITC Invoice Storage Link */}
                <div className="border-t border-gray-200 my-4"></div>
                <a
                  href={nonITCLink.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-transparent text-orange-600 hover:bg-orange-50 hover:text-orange-700 group flex items-center px-2 py-2 text-sm font-medium border-l-4 rounded-md"
                >
                  <nonITCLink.icon className="text-orange-400 group-hover:text-orange-500 mr-3 h-5 w-5" />
                  {nonITCLink.name}
                </a>
              </nav>
            </div>
            <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
              <div className="flex items-center w-full group">
                <div>
                  <img
                    className="inline-block h-9 w-9 rounded-full"
                    src={currentUser?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.displayName || currentUser?.email || 'User')}&background=3b82f6&color=ffffff`}
                    alt=""
                  />
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                    {userProfile?.displayName || currentUser?.email}
                  </p>
                  <button
                    onClick={handleLogout}
                    className="text-xs font-medium text-gray-500 group-hover:text-gray-700 flex items-center"
                  >
                    <ArrowRightOnRectangleIcon className="h-3 w-3 mr-1" />
                    Sign out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <div className="md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3">
          <button
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
        </div>
        <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Layout;
